/**
 * Le cycle de vie d'un export, ce qui s'en déduit sans rien lire ni écrire.
 *
 * Séparé du registre parce que le registre importe la base : ces règles-là
 * doivent pouvoir se vérifier hors ligne, et elles le sont
 * (`scripts/test-compta-cycle.ts`).
 */

export type StatutExport = 'en_cours' | 'genere' | 'transmis' | 'annule' | 'echec';

/**
 * Délai après lequel une génération sans signe de vie est tenue pour morte.
 *
 * Vécu en recette le 24/09/2026 : un conteneur arrêté en pleine génération
 * laisse l'export à `en_cours` pour toujours, et le mois devient impossible à
 * regénérer. La génération écrit son avancement à chaque étape : un export
 * vivant se signale forcément dans ce délai. Valeur retenue par Valérie le
 * 24/09/2026, une génération réelle prenant une dizaine de minutes.
 */
export const DELAI_GENERATION_MORTE_MS = 30 * 60_000;

/**
 * Une génération qui ne donne plus signe de vie. Seul `en_cours` est concerné :
 * un export `genere` ancien est normal — il attend d'être contrôlé, parfois
 * plusieurs jours.
 */
export function generationAbandonnee(e: { statut: StatutExport; updated_at: Date }, maintenant = Date.now()): boolean {
  return e.statut === 'en_cours' && maintenant - e.updated_at.getTime() > DELAI_GENERATION_MORTE_MS;
}

/** Ce que les totaux d'un export portent, pour juger s'il est vide. */
export interface TotauxExport {
  /** Écrit depuis le 25/09/2026 seulement : les exports antérieurs n'en ont pas. */
  piecesTotal?: number;
  rapports?: Record<string, { pieces?: number } | undefined>;
}

/**
 * Une clôture sans aucune pièce : ses classeurs n'ont que leurs en-têtes.
 *
 * Ni déposable ni transmissible — quatre fichiers vides présentés à KPMG comme
 * la clôture du mois seraient pires qu'aucun fichier.
 *
 * LE REPLI SUR LES RAPPORTS N'EST PAS DU CONFORT. La première version ne lisait
 * que `piecesTotal`, écrit depuis le 25/09/2026 : sur une clôture générée
 * AVANT, le champ était absent, la comparaison `undefined === 0` rendait faux,
 * et le garde-fou ne s'appliquait pas — une clôture vide a ainsi pu être
 * déposée et marquée transmise en préproduction. Tout export porte en revanche
 * ses rapports et leur nombre de pièces : c'est sur eux qu'on se rabat.
 */
export function clotureVide(totaux: unknown): boolean {
  const t = (totaux ?? {}) as TotauxExport;
  if (typeof t.piecesTotal === 'number') return t.piecesTotal === 0;
  const rapports = Object.values(t.rapports ?? {});
  // Aucun rapport : la génération n'a pas abouti, on n'affirme rien.
  return rapports.length > 0 && rapports.every((r) => (r?.pieces ?? 0) === 0);
}

/**
 * Peut-on annuler cette clôture, et sinon pourquoi.
 *
 * Une clôture TRANSMISE ne s'annule pas : ses pièces sont chez KPMG, les
 * libérer les ferait repartir en doublon. Une seule exception, bornée au
 * 25/09/2026 : une clôture transmise VIDE, qui n'a réservé aucune pièce. Elle
 * ne peut donc rien libérer, et c'est le seul moyen de retirer de l'historique
 * une transmission qui n'a rien transmis.
 */
export function refusAnnulation(exp: { statut: StatutExport; pieces: number; totaux: unknown; transmis_at?: Date | null }): string | null {
  if (exp.statut === 'annule') return 'Export déjà annulé.';
  if (exp.statut !== 'transmis') return null;
  if (exp.pieces === 0 && clotureVide(exp.totaux)) return null;
  const quand = exp.transmis_at ? ` le ${exp.transmis_at.toISOString().slice(0, 10)}` : '';
  return (
    `Export transmis${quand} : il ne peut plus être annulé. Ses ${exp.pieces} pièce(s) sont chez KPMG ; ` +
    `les libérer les ferait repartir en doublon à la prochaine clôture.`
  );
}

/**
 * Ce que l'écran peut dire des références « Annule et remplace » introuvables
 * d'une clôture, selon son statut.
 *
 * Le remède n'est pas le même dans les trois cas, et en proposer un faux
 * indique un geste qui n'existe pas :
 * - `a_corriger` : la clôture est générée, pas encore partie ; on corrige dans
 *   Books, on l'annule et on la regénère ;
 * - `parties` : transmise, elle ne s'annule plus (`refusAnnulation`) ; seul le
 *   cabinet peut encore recevoir la référence ;
 * - `liberees` : annulée, elle garde ses totaux mais a libéré ses pièces, qui
 *   repartiront avec la prochaine clôture — c'est avant celle-là qu'on corrige.
 */
export type SortReferencesIntrouvables = 'a_corriger' | 'parties' | 'liberees';

export function sortReferencesIntrouvables(statut: StatutExport): SortReferencesIntrouvables {
  if (statut === 'transmis') return 'parties';
  if (statut === 'annule') return 'liberees';
  return 'a_corriger';
}

/**
 * Les statuts d'une clôture qui interdisent d'en produire une autre sur le
 * même mois.
 *
 * `transmis` en fait partie depuis le 25/09/2026 : sans lui, un mois déjà
 * envoyé acceptait une seconde génération, qui lisait Books pendant dix
 * minutes pour rendre des fichiers vides — toutes ses pièces étant réservées.
 *
 * `annule` et `echec` n'y sont PAS, délibérément : la première a libéré ses
 * pièces et le mois est à refaire ; la seconde n'a rien produit, et bloquer
 * dessus rendrait le mois définitivement ingénérable.
 */
export const STATUTS_BLOQUANT_GENERATION: readonly StatutExport[] = ['en_cours', 'genere', 'transmis'];

/**
 * Cette clôture empêche-t-elle une nouvelle génération de son mois ?
 *
 * Une génération ABANDONNÉE ne bloque pas : elle sera déclarée morte, et le
 * mois redevient générable.
 */
export function bloqueNouvelleGeneration(e: { statut: StatutExport; updated_at: Date }, maintenant = Date.now()): boolean {
  if (!STATUTS_BLOQUANT_GENERATION.includes(e.statut)) return false;
  return !generationAbandonnee(e, maintenant);
}

/**
 * Le refus opposé à une génération tant que le REGISTRE EST VIDE.
 *
 * Sans registre amorcé, une clôture ne sait pas ce qui a déjà été envoyé à
 * KPMG : elle remonterait TOUT l'historique de Books, et renverrait au cabinet
 * des pièces déjà comptabilisées. Le bandeau de l'écran le disait, mais lui
 * seul : rien n'empêchait un clic sur « Générer ». Le refus vit donc ici, et le
 * service l'oppose AVANT le premier appel à Books.
 */
export function messageRegistreNonAmorce(): string {
  return (
    'Le registre n’est pas encore amorcé : une génération remonterait tout l’historique de Books et renverrait à KPMG des pièces déjà envoyées. ' +
    'Passez d’abord par l’écran d’initialisation, avec les fichiers des envois faits à la main.'
  );
}

/**
 * Le refus opposé à une nouvelle génération : il NOMME l'état et la date de la
 * clôture qui bloque, sans quoi on ne sait pas quoi faire pour débloquer.
 */
export function messageClotureBloquante(mois: string, e: { statut: StatutExport; genere_at: Date; transmis_at?: Date | null }): string {
  const jour = (d: Date) => d.toISOString().slice(0, 10);
  const etat =
    e.statut === 'transmis'
      ? `transmise le ${jour(e.transmis_at ?? e.genere_at)}`
      : e.statut === 'en_cours'
        ? 'en cours de génération'
        : `générée le ${jour(e.genere_at)} et en attente de contrôle`;
  return (
    `${mois} porte déjà une clôture (${etat}) : ses pièces sont réservées, une seconde génération rendrait des fichiers vides. ` +
    `L’annuler d’abord si elle doit être refaite.`
  );
}
